import {
    Box,
    Button,
    capitalize,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {AnwesenheitInfo} from "../../api";
import DataTable from "../data/DataTable";
import Grid from "@mui/material/Grid2";
import {useGetBenutzer} from "../../client/hooks";
import {useMe} from "../../client/queries";
import {formatGermanNumber} from "../../utils/fomatters";
import useCustomFilters from "../data/useCustomFilters";

export type MeineAnwesenheitenFilter = {
    search?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<MeineAnwesenheitenFilter>, "filter"> &
    Partial<Pick<DataRequest<MeineAnwesenheitenFilter>, "filter">>;

const tableName: string = "meine-anwesenheiten"

export default function MyAnwesenheitenDataTable({...input}: Props) {
    const request = useDataRequest<MeineAnwesenheitenFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp request={request} filteredStateFilter={filteredState}/>
            <MyAnwesenheitenResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<MeineAnwesenheitenFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<MeineAnwesenheitenFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const ladeBenutzer = useGetBenutzer();
    const ladeMe = useMe();
    const [az_saldo, setAzSaldo] = useState("");
    const {t: translation} = useT();

    async function getAzSaldo() {
        await ladeBenutzer(ladeMe.benutzerId!!).then((benutzer) => {
            setAzSaldo(benutzer.azSaldo?.toString() ?? "0");
        });
    }

    getAzSaldo();

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-meine-anwesenheit");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Grid size={{xs: 1.5, md: 1.5}}>
                        <Stack sx={{ml: 2, textAlign: "center"}}>
                            <Typography>Mein AZ-Saldo</Typography>
                            <Box>{az_saldo}</Box>
                        </Stack>
                    </Grid>
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-meine-anwesenheit");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function MyAnwesenheitenResults({
                                    request,
                                    setFilteredState,
                                }: {
    request: DataRequestState<MeineAnwesenheitenFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<MeineAnwesenheitenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadAnwesenheiten: DataLoader<MeineAnwesenheitenFilter, AnwesenheitInfo> =
        useCallback(
            async (params) =>
                (await getApi()).anwesenheiten.sucheMeineAnwesenheiten({...params}),
            [getApi]
        );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadAnwesenheiten}
            onRowClick={(row, event) => {
                if (event.ctrlKey || event.metaKey) {
                    window.open("/meine-anwesenheiten/" + row.id, "_blank");
                } else {
                    navigate("/meine-anwesenheiten/" + row.id);
                }
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<AnwesenheitInfo>> {
    const {t: translation} = useT("anwesenheit");

    return useMemo(
        () => [
            {
                field: "datum",
                headerName: capitalize(translation("anwesenheit-attributes-date")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                    }
                    return "";
                },
            },
            {
                field: "start",
                headerName: capitalize(translation("anwesenheit-attributes-start")),
                filterable: false,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                        });
                    }
                    return "";
                },
            },
            {
                field: "ende",
                headerName: capitalize(translation("anwesenheit-attributes-end")),
                filterable: false,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                        });
                    }
                    return "";
                },
            },
            {
                field: "arbeitszeitNetto",
                headerName: capitalize(translation("anwesenheit-attributes-net-worktime")),
                flex: 0.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "fahrzeiten",
                headerName: capitalize(translation("anwesenheit-attributes-drive-times")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "bezahltePause",
                headerName: capitalize(translation("anwesenheit-attributes-paid-pause")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "unbezahltePause",
                headerName: capitalize(translation("anwesenheit-attributes-unpaid-pause")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "arbeitszeitBrutto",
                headerName: capitalize(translation("anwesenheit-attributes-paid-worktime")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
        ],
        [translation]
    );
}
